import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Clock, MapPin } from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import ContactForm from '@/components/ContactForm';
import LightPageHero from '@/components/LightPageHero';

const contactItems = [
  {
    icon: Mail,
    label: 'Email',
    value: 'info@aifinity.se',
    href: 'mailto:info@aifinity.se',
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.25)',
  },
  {
    icon: Clock,
    label: 'Response time',
    value: 'Within 24 hours',
    href: null,
    gradient: 'from-violet-500 to-purple-600',
    glow: 'rgba(139,92,246,0.25)',
  },
  {
    icon: MapPin,
    label: 'Location',
    value: 'Sweden',
    href: null,
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.25)',
  },
];

const contactFor = [
  'We automate the repetitive',
  'We build SaaS products that are actually used',
  'We write code, not PowerPoints',
  'AI chatbots and agents that work for you',
  '3D printing — from file to physical product',
];

const EnContactPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CorporateNavbar />

      <LightPageHero
        badge="Contact"
        heading={<>Tell us what you want to stop<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>doing manually</span></>}
        subtext="We get back to you within 24 hours with a concrete proposal."
      />

      {/* ── About section ── */}
      <section className="py-20 bg-white border-b border-border-light">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-5">About Aifinity</span>
              <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight mb-6">
                Tomorrow's technology.<br />
                <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 bg-clip-text text-transparent">
                  Today's results.
                </span>
              </h2>
              <p className="text-[15px] text-text-secondary leading-relaxed mb-4">
                Aifinity AB is a Swedish tech company focused on AI automation, SaaS products and smart business solutions for modern B2B companies. We combine deep technical expertise with a pragmatic view of business value — we deliver what actually works.
              </p>
              <p className="text-[15px] text-text-secondary leading-relaxed mb-6">
                We believe in fast delivery, measurable results and solutions that get used. From discovery to live in weeks, not months.
              </p>
              <ul className="space-y-3">
                {[
                  'AI agents and automation that save hours every day',
                  'SaaS products built to scale',
                  'Seamless integration with existing systems',
                  'Fast deliveries — you see results within weeks',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3 text-[14px] text-text-secondary">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-indigo mt-2 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Delivery in 2–4 weeks', desc: 'From idea to live — no long ramp-ups',      color: 'from-indigo-500 to-violet-600' },
                { label: 'Direct developer contact', desc: 'You talk to the person actually building', color: 'from-violet-500 to-purple-600' },
                { label: 'Built for your scale',  desc: 'Tailored for SMBs and associations',       color: 'from-amber-400 to-orange-500' },
                { label: 'Easy to test',          desc: 'Start small — scale when you see value',   color: 'from-emerald-400 to-teal-500' },
              ].map((s) => (
                <div
                  key={s.label}
                  className="bg-surface-subtle rounded-2xl border border-border-light p-4 sm:p-6 text-center"
                >
                  <div className={`inline-flex w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br ${s.color} items-center justify-center mb-2 sm:mb-3`} />
                  <p className="text-sm sm:text-[15px] font-bold text-text-primary mb-1">{s.label}</p>
                  <p className="text-[11px] sm:text-xs text-text-secondary leading-tight">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact Info Cards ── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
            {contactItems.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -3 }}
                className="bg-white rounded-2xl border border-border-light p-6 shadow-sm hover:shadow-xl transition-all duration-300 text-center"
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 20px 50px ${item.glow}`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
              >
                <div className={`inline-flex w-10 h-10 rounded-xl bg-gradient-to-br ${item.gradient} items-center justify-center mb-3 shadow-md`}
                  style={{ boxShadow: `0 4px 12px ${item.glow}` }}>
                  <item.icon className="w-4 h-4 text-white" />
                </div>
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-widest mb-1">{item.label}</p>
                {item.href ? (
                  <a href={item.href} className="text-sm font-semibold text-text-primary hover:text-brand-indigo transition-colors">
                    {item.value}
                  </a>
                ) : (
                  <p className="text-sm font-semibold text-text-primary">{item.value}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Form + Info ── */}
      <section className="py-16 bg-surface-subtle">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-5xl mx-auto">

            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-3"
            >
              <h2 className="text-2xl font-extrabold text-text-primary mb-2">Send a message</h2>
              <p className="text-sm text-text-secondary mb-3">Briefly describe what takes time today. Examples of what you can write:</p>
              <ul className="space-y-1.5 mb-6">
                {[
                  '"We get too many emails to handle manually"',
                  '"Bookings are done manually today"',
                  '"We want to automate customer support"',
                ].map(ex => (
                  <li key={ex} className="text-[13px] text-text-muted italic pl-3 border-l-2 border-indigo-200">{ex}</li>
                ))}</ul>
              <div className="bg-white rounded-2xl border border-border-light shadow-sm p-5 sm:p-8">
                <ContactForm />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-2"
            >
              <h2 className="text-2xl font-extrabold text-text-primary mb-6">We can help with</h2>
              <div className="bg-white rounded-2xl border border-border-light shadow-sm p-6 mb-6">
                <ul className="space-y-3">
                  {contactFor.map(item => (
                    <li key={item} className="flex items-start gap-3 text-[15px] text-text-secondary">
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-indigo mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-5 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50">
                <p className="text-sm font-semibold text-text-primary mb-1">Fast response guaranteed</p>
                <p className="text-sm text-text-secondary leading-relaxed">
                  We respond to all enquiries within 24 hours on weekdays. In a hurry? Email us directly at{' '}
                  <a href="mailto:info@aifinity.se" className="text-brand-indigo font-semibold">info@aifinity.se</a>.
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default EnContactPage;
